import React, { useEffect, useRef } from 'react';
import { createChart, IChartApi, ISeriesApi, LineData, Time, LineSeries, SeriesMarker, createSeriesMarkers } from 'lightweight-charts';
import { extractBaseCurrency } from '@/utils/symbols';
import { CumulativePnlChartProps, PositionMarker } from './CumulativePnlChart.types';

export const CumulativePnlChart: React.FC<CumulativePnlChartProps> = ({
  cumulativePnlData,
  positionMarkers,
  height,
  className = '',
}) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const lineSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  // Initialize chart
  useEffect(() => {
    if (!chartContainerRef.current || !cumulativePnlData.length) {
      // Cleanup if no data
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
        lineSeriesRef.current = null;
      }
      return;
    }

    // Wait for container to have dimensions
    const initChart = () => {
      if (!chartContainerRef.current) return;

      const container = chartContainerRef.current;
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;
      
      // Ensure container has dimensions before creating chart
      if (containerWidth === 0 || containerHeight === 0) {
        // Retry after a short delay
        setTimeout(initChart, 100);
        return;
      }

      // Clean up existing chart if any
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
        lineSeriesRef.current = null;
      }

      // Clean up existing tooltip if any
      if (tooltipRef.current?.parentNode) {
        tooltipRef.current.remove();
        tooltipRef.current = null;
      }

      // Create chart
      const chart = createChart(container, {
        width: containerWidth,
        height: containerHeight,
        layout: {
          background: { color: '#1a1b20' },
          textColor: '#717680',
        },
        grid: {
          vertLines: { color: '#2e2f35', visible: true },
          horzLines: { color: '#2e2f35', visible: true },
        },
        timeScale: {
          timeVisible: true,
          secondsVisible: false,
          borderColor: 'rgba(112, 132, 153, 0.2)',
        },
        rightPriceScale: {
          borderColor: 'rgba(112, 132, 153, 0.2)',
          visible: true,
          autoScale: true,
          scaleMargins: {
            top: 0.1,
            bottom: 0.1,
          },
        },
        crosshair: {
          vertLine: {
            color: '#2e2f35',
            width: 1,
            style: 1,
          },
          horzLine: {
            color: '#2e2f35',
            width: 1,
            style: 1,
          },
        },
      });

      chartRef.current = chart;

      // Create line series
      const lineSeries = chart.addSeries(LineSeries, {
        color: '#6ab2f2',
        lineWidth: 2,
        priceFormat: {
          type: 'price',
          precision: 2,
          minMove: 0.01,
        },
      });

      lineSeriesRef.current = lineSeries;

      // Convert cumulative PnL data to chart format
      // Sort by date to ensure proper ordering
      const sortedData = [...cumulativePnlData].sort((a, b) => 
        a.date.localeCompare(b.date)
      );

      const chartData: LineData[] = sortedData.map((point) => {
        // Parse date string "YYYY-MM-DD" and convert to UTC timestamp
        // Add time component to ensure proper date parsing
        const dateStr = point.date.includes('T') ? point.date : `${point.date}T00:00:00Z`;
        const date = new Date(dateStr);
        return {
          time: (date.getTime() / 1000) as Time,
          value: point.cumulativePnl,
        };
      });

      lineSeries.setData(chartData);

      // Create a map of date -> markers for quick lookup
      const markersByDate = new Map<string, PositionMarker[]>();
      if (positionMarkers?.length) {
        positionMarkers.forEach((marker) => {
          const dateKey = marker.date;
          const existingMarkers = markersByDate.get(dateKey) || [];
          markersByDate.set(dateKey, [...existingMarkers, marker]);
        });
      }

      // Add simplified markers (one per date, showing token names)
      if (markersByDate.size > 0) {
        const chartMarkers: SeriesMarker<Time>[] = [];
        
        markersByDate.forEach((markers, dateKey) => {
          if (!markers) return;
          // Extract all unique token symbols for this date
          const symbols = markers.map(m => extractBaseCurrency(m.symbol));
          const uniqueSymbols = [...new Set(symbols)];
          
          // Create marker text: if multiple, show "BTC, ETH, ..." or just "BTC" if single
          const markerText = uniqueSymbols.length > 1 
            ? uniqueSymbols.join(', ')
            : uniqueSymbols[0] || '';

          // Use the first marker's PnL for color (or average if multiple)
          const markerColor = '#6ab2f2';

          const dateStr = dateKey.includes('T') ? dateKey : `${dateKey}T00:00:00Z`;
          const date = new Date(dateStr);
          const time = (date.getTime() / 1000) as Time;

          chartMarkers.push({
            time,
            position: 'belowBar' as const,
            color: markerColor,
            shape: 'arrowUp' as const,
            text: markerText,
            size: 1,
          });
        });

        createSeriesMarkers(lineSeries, chartMarkers);
      }

      // Create tooltip element
      const tooltip = document.createElement('div');
      tooltip.style.cssText = `
        position: absolute;
        display: none;
        padding: 8px 12px;
        box-sizing: border-box;
        font-size: 12px;
        text-align: left;
        z-index: 1000;
        pointer-events: none;
        border: 1px solid;
        border-radius: 6px;
        background: #1a1b20;
        color: #e5e7eb;
        border-color: #6ab2f2;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.3);
        max-width: 200px;
        font-family: -apple-system, BlinkMacSystemFont, 'Trebuchet MS', Roboto, Ubuntu, sans-serif;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
      `;
      container.appendChild(tooltip);
      tooltipRef.current = tooltip;

      // Helper function to format exchange display name
      const formatExchangeName = (exchange: string, exchangeType?: string): string => {
        if (exchange === 'binance') {
          return 'Binance Futures';
        }
        const capitalized = exchange.charAt(0).toUpperCase() + exchange.slice(1);
        return exchangeType === 'futures' ? `${capitalized} Futures` : capitalized;
      };

      // Helper function to format marker text
      const formatMarkerText = (marker: PositionMarker, baseSymbol: string): string => {
        const isAutoTrade = marker.tradeOrigin === 'AUTO_TRADE';
        const isAlert = marker.tradeOrigin === 'NEWS_ALERT' || marker.tradeOrigin === 'PRICE_ALERT';

        if (isAutoTrade && marker.alert) {
          const { alert } = marker;
          const exchangeDisplayName = formatExchangeName(alert.exchange, alert.exchangeType);
          const actionText = alert.type === 'new_coin' ? 'Listed' : 'Delisted';
          return `🤖 ${baseSymbol} ${actionText} on ${exchangeDisplayName}`;
        }

        if (isAlert && marker.alert) {
          const { alert } = marker;
          const exchangeDisplayName = formatExchangeName(alert.exchange, alert.exchangeType);
          const actionText = alert.type === 'new_coin' ? 'listing' : 'delisting';
          return `${baseSymbol} ${actionText} on ${exchangeDisplayName}`;
        }

        if (isAlert) {
          const exchangeDisplayName = marker.exchangeName === 'Binance' 
            ? 'Binance Futures' 
            : marker.exchangeName;
          return `${baseSymbol} listing on ${exchangeDisplayName}`;
        }

        return baseSymbol;
      };

      // Helper function to get PnL color
      const getPnlColor = (pnlPercentage: number | null = null): string => {
        const pnl = pnlPercentage ?? 0;
        if (pnl > 0) return '#17b26a';
        if (pnl < 0) return '#d92d20';
        return '#717680';
      };

      // Subscribe to crosshair move to show tooltip
      chart.subscribeCrosshairMove((param) => {
        if (
          param.point === undefined ||
          !param.time ||
          param.point.x < 0 ||
          param.point.x > container.clientWidth ||
          param.point.y < 0 ||
          param.point.y > container.clientHeight
        ) {
          tooltip.style.display = 'none';
          return;
        }

        // Convert time to date string (YYYY-MM-DD)
        const timeValue = param.time as number;
        const date = new Date(timeValue * 1000);
        const dateStr = date.toISOString().split('T')[0];

        // Check if there are markers for this date
        const markersForDate = markersByDate.get(dateStr);
        if (!markersForDate?.length) {
          tooltip.style.display = 'none';
          return;
        }

        // Get the data point for cumulative PnL
        const seriesData = param.seriesData.get(lineSeries);

        // Build tooltip content
        let tooltipContent = ``
        
        // Helper to format marker entry
        const formatMarkerEntry = (marker: PositionMarker, index: number): string => {
          const baseSymbol = extractBaseCurrency(marker.symbol);
          const markerText = formatMarkerText(marker, baseSymbol);
          const pnlColor = getPnlColor(marker.pnlPercentage);
          
          const pnlPercentage = marker.pnlPercentage;
          const hasPnl = pnlPercentage !== null;
          const pnlSign = hasPnl && pnlPercentage >= 0 ? '+' : '';
          const pnlText = hasPnl 
            ? `${pnlSign}$${marker.realizedPnl?.toFixed(2)}`
            : 'N/A';
          
          let entry = '';
          if (index > 0) {
            entry += '<div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid #2e2f35;"></div>';
          }
          entry += `<div style="color: #e5e7eb; font-size: 11px; margin-bottom: 2px;">${markerText}</div>`;
          entry += `<div style="color: ${pnlColor}; font-size: 10px;">PnL: ${pnlText}</div>`;
          return entry;
        };

        markersForDate.forEach((marker, index) => {
          tooltipContent += formatMarkerEntry(marker, index);
        });
        tooltipContent += '</div>';

        tooltip.innerHTML = tooltipContent;
        tooltip.style.display = 'block';

        // Position tooltip
        const tooltipWidth = 200;
        const tooltipHeight = tooltip.offsetHeight;
        const tooltipMargin = 15;

        let x = param.point.x - tooltipWidth / 2;
        x = Math.max(10, Math.min(container.clientWidth - tooltipWidth - 10, x));

        const coordinate = seriesData && 'value' in seriesData
          ? lineSeries.priceToCoordinate(seriesData.value)
          : null;
        
        let y: number;
        if (coordinate !== null && coordinate !== undefined) {
          y = coordinate - tooltipHeight - tooltipMargin;
          if (y < 0) {
            y = coordinate + tooltipMargin;
          }
        } else {
          y = param.point.y - tooltipHeight - tooltipMargin;
          if (y < 0) {
            y = param.point.y + tooltipMargin;
          }
        }
        y = Math.max(10, Math.min(container.clientHeight - tooltipHeight - 10, y));

        tooltip.style.left = `${x}px`;
        tooltip.style.top = `${y}px`;
      });

      chart.timeScale().fitContent();

      // Force a resize after data is set to ensure proper rendering
      requestAnimationFrame(() => {
        if (chartRef.current && container) {
          chartRef.current.applyOptions({
            width: container.clientWidth,
            height: container.clientHeight,
          });
        }
      });
    };

    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      setTimeout(initChart, 150); // Small delay to ensure drawer animation completes
    });

    // Handle resize
    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        chartRef.current.applyOptions({
          width: chartContainerRef.current.clientWidth,
          height: chartContainerRef.current.clientHeight,
        });
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (tooltipRef.current?.parentNode) {
        tooltipRef.current.remove();
        tooltipRef.current = null;
      }
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
        lineSeriesRef.current = null;
      }
    };
  }, [cumulativePnlData, positionMarkers]);

  return (
    <div
      ref={chartContainerRef}
      className={`w-full rounded-lg relative ${className}`}
      style={{ height: height ? `${height}px` : '100%' }}
    />
  );
};
